import axiosInstance from "../../services/API";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// 1) GET OR CREATE CHAT ROOM
export const getAndCreateChatRoom = createAsyncThunk("getAndCreateChatRoom", async (user_id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/messages/${user_id}/get-or-create/`);
        const result = response.data;
        return result;
    } catch (error) {
        console.error("Error:", error.response?.data);
        return rejectWithValue(error.response?.data || { detail: error.message });
    }
});

// 2) CHAT ROOM DETAIL LIST
export const getChatRoomDetailList = createAsyncThunk("getChatRoomDetailList", async () => {
    try {
        const response = await axiosInstance.get("/messages/chat_room_detail_list/");
        const result = response.data;
        return result;
    } catch (error) {
        console.error("Error:", error.response.data);
        throw error;
    }
});

// 3) SEND MESSAGE
export const sendMessage = createAsyncThunk("sendMessage", async ({ chatRoomId, messageData }) => {
    try {
        const response = await axiosInstance.post(`/messages/${chatRoomId}/send/`, messageData);
        const result = response.data;
        return result;
    } catch (error) {
        console.error("Error:", error.response.data);
        throw error;
    }
});

// 4) DELETE CHAT ROOM
export const deleteChatRoom = createAsyncThunk("deleteChatRoom", async (chatRoomId) => {
    try {
        const response = await axiosInstance.post(`/messages/delete-room/${chatRoomId}/`);
        const result = response.data;
        return { result, chatRoomId };
    } catch (error) {
        console.error("Error:", error.response.data);
        throw error;
    }
});

// 5) UNREAD MESSAGES
export const getUnreadMessages = createAsyncThunk("getUnreadMessages", async () => {
    try {
        const response = await axiosInstance.get("/messages/unread_messages/");
        const result = response.data;
        return result;
    } catch (error) {
        console.error("Error:", error.response.data);
        throw error;
    }
});

// 6) MARK ALL MESSAGES AS READ
export const markMessagesAsRead = createAsyncThunk("markMessagesAsRead", async (chatRoomId) => {
    try {
        const response = await axiosInstance.post(`/messages/mark_messages_as_read/${chatRoomId}/`);
        const result = response.data;
        return { result, chatRoomId };
    } catch (error) {
        console.error("Error:", error.response.data);
        throw error;
    }
});

// 7) MESSAGE REQUEST LIST
export const getMessageRequestList = createAsyncThunk("getMessageRequestList", async () => {
    try {
        const response = await axiosInstance.get("/messages/message_request_list/");
        const result = response.data;
        return result;
    } catch (error) {
        console.error("Error:", error.response.data);
        throw error;
    }
});

// 8) ACCEPT MESSAGE REQUEST
export const acceptMessageRequest = createAsyncThunk("acceptMessageRequest", async (chatRoomId) => {
    try {
        const response = await axiosInstance.post(`/messages/accept/${chatRoomId}/`);
        const result = response.data;
        return { result, chatRoomId };
    } catch (error) {
        console.error("Error:", error.response.data);
        throw error;
    }
});

// 9) REJECT MESSAGE REQUEST
export const rejectMessageRequest = createAsyncThunk("rejectMessageRequest", async (chatRoomId) => {
    try {
        const response = await axiosInstance.post(`/messages/reject/${chatRoomId}/`);
        const result = response.data;
        return { result, chatRoomId };
    } catch (error) {
        console.error("Error:", error.response.data);
        throw error;
    }
});

// 10) ADD REACTION TO A MESSAGE
export const reactToMessage = createAsyncThunk("reactToMessage", async (messageId) => {
    try {
        const response = await axiosInstance.post(`/messages/${messageId}/react/`);
        const result = response.data;
        return result;
    } catch (error) {
        console.error("Error:", error.response.data);
        throw error;
    }
});

// 12) DELETE A MESSAGE
export const deleteMessage = createAsyncThunk("deleteMessage", async (messageId) => {
    try {
        await axiosInstance.delete(`/messages/message/${messageId}/delete/`);
        return { messageId };
    } catch (error) {
        console.error("Error:", error.response?.data);
        throw error;
    }
});

// 17) EDIT A MESSAGE
export const editMessage = createAsyncThunk("editMessage", async ({ messageId, message }) => {
    try {
        const response = await axiosInstance.patch(`/messages/message/${messageId}/edit/`, { message });
        return response.data;
    } catch (error) {
        console.error("Error:", error.response?.data);
        throw error;
    }
});

// 11) REPORT A MESSAGE
export const reportMessage = createAsyncThunk("reportMessage", async (data) => {
    try {
        const response = await axiosInstance.post(`messages/report-user/`, data);
        const result = response.data;
        return result;
    } catch (error) {
        console.error("Error:", error.response?.data || error.message);
        throw error;
    }
});

// Normalize the varied error shapes the report-message endpoint can return into a single message
function parseReportChatMessageError(responseData) {
    if (!responseData) return "Failed to report message";
    if (responseData.detail) return responseData.detail;
    if (Array.isArray(responseData.non_field_errors) && responseData.non_field_errors.length > 0) {
        return responseData.non_field_errors[0];
    }
    if (Array.isArray(responseData.message_id) && responseData.message_id.length > 0) {
        return responseData.message_id[0];
    }
    if (responseData.reason?.message) return responseData.reason.message;
    if (responseData.sub_reason?.message) return responseData.sub_reason.message;
    return "Failed to report message";
}

// 18) REPORT A SINGLE CHAT MESSAGE
export const reportChatMessage = createAsyncThunk("reportChatMessage", async ({ messageId, data }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/messages/message/${messageId}/report/`, data);
        return response.data;
    } catch (error) {
        const responseData = error.response?.data;
        return rejectWithValue({ message: parseReportChatMessageError(responseData), status: error.response?.status });
    }
});

// 13) MUTE / UNMUTE MESSAGE CHAT
export const muteUnmuteMessageChat = createAsyncThunk("muteUnmuteMessageChat", async (chatRoomId) => {
    try {
        const response = await axiosInstance.post(`/messages/toggle-mute/${chatRoomId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
});

// 14) TOTAL UNREAD MESSAGE COUNT
export const totalUnreadMessageCount = createAsyncThunk("totalUnreadMessageCount", async () => {
    try {
        const response = await axiosInstance.get(`/messages/chat-unread-count/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
});

// 15) RECOMMENDED FRIENDS
export const recommendedFriends = createAsyncThunk("recommendedFriends", async () => {
    try {
        const response = await axiosInstance.get(`/messages/friends/recommended/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
});

// 16) SEARCH MESSAGES
export const searchMessages = createAsyncThunk("searchMessages", async (query, { signal }) => {
    try {
        const response = await axiosInstance.get(`/messages/search/`, { params: { q: query }, signal });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
});

export const MessagesSlice = createSlice({
    name: "Messages",
    initialState: {
        messagesList: [],
        chatRoomData: [],
        chatRoomList: [],
        unreadMessages: [],
        messageRequests: [],
        recommendedFriendsList: [],
        totalUnreadCount: null,
        searchResults: [],
        searchLoading: false,
        loading: false,
        error: null,
        sendMessageLoading: false,
        deleteRoomLoading: false,
        deleteMessageLoading: false,
        markReadLoading: false,
        acceptRequestLoading: false,
        rejectRequestLoading: false,
    },
    reducers: {},
    extraReducers: (builder) => {
        // 1) Get or Create Chat Room
        builder.addCase(getAndCreateChatRoom.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getAndCreateChatRoom.fulfilled, (state, action) => {
            state.loading = false;
            state.chatRoomData = action.payload;
            state.error = null;
        });
        builder.addCase(getAndCreateChatRoom.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload?.detail || action.error.message;
        });

        // Chat Room Detail List
        builder.addCase(getChatRoomDetailList.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getChatRoomDetailList.fulfilled, (state, action) => {
            state.loading = false;
            // Always store as array for chatRoomList
            if (Array.isArray(action.payload)) {
                state.chatRoomList = action.payload;
            } else if (action.payload && Array.isArray(action.payload.chat_room_detail_list)) {
                state.chatRoomList = action.payload.chat_room_detail_list;
            } else {
                state.chatRoomList = [];
            }
            state.error = null;
        });
        builder.addCase(getChatRoomDetailList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // Send Message
        builder.addCase(sendMessage.pending, (state) => {
            state.sendMessageLoading = true;
            state.error = null;
        });
        builder.addCase(sendMessage.fulfilled, (state, action) => {
            state.sendMessageLoading = false;
            // Add the new message to the messages list
            state.messagesList.push(action.payload);
            state.error = null;
        });
        builder.addCase(sendMessage.rejected, (state, action) => {
            state.sendMessageLoading = false;
            state.error = action.error.message;
        });

        // Delete Chat Room
        builder.addCase(deleteChatRoom.pending, (state) => {
            state.deleteRoomLoading = true;
            state.error = null;
        });
        builder.addCase(deleteChatRoom.fulfilled, (state, action) => {
            state.deleteRoomLoading = false;
            // Remove the deleted chat room from the list (ensure array)
            if (Array.isArray(state.chatRoomList)) {
                state.chatRoomList = state.chatRoomList.filter(room => room.id !== action.payload.chatRoomId);
            }
            state.error = null;
        });
        builder.addCase(deleteChatRoom.rejected, (state, action) => {
            state.deleteRoomLoading = false;
            state.error = action.error.message;
        });

        // Unread Messages
        builder.addCase(getUnreadMessages.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getUnreadMessages.fulfilled, (state, action) => {
            state.loading = false;
            state.unreadMessages = action.payload;
            state.error = null;
        });
        builder.addCase(getUnreadMessages.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // Mark Messages as Read
        builder.addCase(markMessagesAsRead.pending, (state) => {
            state.markReadLoading = true;
            state.error = null;
        });
        builder.addCase(markMessagesAsRead.fulfilled, (state, action) => {
            state.markReadLoading = false;
            state.unreadMessages = state.unreadMessages.filter(msg => msg.chatRoomId !== action.payload.chatRoomId);
            state.error = null;
        });
        builder.addCase(markMessagesAsRead.rejected, (state, action) => {
            state.markReadLoading = false;
            state.error = action.error.message;
        });

        // Message Request List
        builder.addCase(getMessageRequestList.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getMessageRequestList.fulfilled, (state, action) => {
            state.loading = false;
            state.messageRequests = action.payload;
            state.error = null;
        });
        builder.addCase(getMessageRequestList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // Accept Message Request
        builder.addCase(acceptMessageRequest.pending, (state) => {
            state.acceptRequestLoading = true;
            state.error = null;
        });
        builder.addCase(acceptMessageRequest.fulfilled, (state, action) => {
            state.acceptRequestLoading = false;
            let requests = [];
            if (Array.isArray(state.messageRequests?.message_request_list)) {
                requests = state.messageRequests.message_request_list;
            } else if (Array.isArray(state.messageRequests)) {
                requests = state.messageRequests;
            }
            requests = requests.filter(request => request.id !== action.payload.chatRoomId);
            state.messageRequests = requests;
            state.error = null;
        });
        builder.addCase(acceptMessageRequest.rejected, (state, action) => {
            state.acceptRequestLoading = false;
            state.error = action.error.message;
        });

        // Reject Message Request
        builder.addCase(rejectMessageRequest.pending, (state) => {
            state.rejectRequestLoading = true;
            state.error = null;
        });
        builder.addCase(rejectMessageRequest.fulfilled, (state, action) => {
            state.rejectRequestLoading = false;
            let requests = [];
            if (Array.isArray(state.messageRequests?.message_request_list)) {
                requests = state.messageRequests.message_request_list;
            } else if (Array.isArray(state.messageRequests)) {
                requests = state.messageRequests;
            }
            requests = requests.filter(request => request.id !== action.payload.chatRoomId);
            state.messageRequests = requests;
            state.error = null;
        });
        builder.addCase(rejectMessageRequest.rejected, (state, action) => {
            state.rejectRequestLoading = false;
            state.error = action.error.message;
        });

        // Delete Message
        builder.addCase(deleteMessage.pending, (state) => {
            state.deleteMessageLoading = true;
            state.error = null;
        });
        builder.addCase(deleteMessage.fulfilled, (state) => {
            state.deleteMessageLoading = false;
            state.error = null;
        });
        builder.addCase(deleteMessage.rejected, (state, action) => {
            state.deleteMessageLoading = false;
            state.error = action.error.message;
        });

        // Edit Message
        builder.addCase(editMessage.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(editMessage.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
        });
        builder.addCase(editMessage.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // React to Message
        builder.addCase(reactToMessage.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(reactToMessage.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
        });
        builder.addCase(reactToMessage.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // Report Message
        builder.addCase(reportMessage.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(reportMessage.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
        });
        builder.addCase(reportMessage.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // Report Chat Message
        builder.addCase(reportChatMessage.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(reportChatMessage.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
        });
        builder.addCase(reportChatMessage.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Mute/Unmute Message Chat
        builder.addCase(muteUnmuteMessageChat.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(muteUnmuteMessageChat.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
        });
        builder.addCase(muteUnmuteMessageChat.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // Total Unread Message Count
        builder.addCase(totalUnreadMessageCount.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(totalUnreadMessageCount.fulfilled, (state, action) => {
            state.loading = false;
            state.totalUnreadCount = action.payload;
            state.error = null;
        });
        builder.addCase(totalUnreadMessageCount.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // Recommended Friends
        builder.addCase(recommendedFriends.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(recommendedFriends.fulfilled, (state, action) => {
            state.loading = false;
            state.recommendedFriendsList = action.payload;
            state.error = null;
        });
        builder.addCase(recommendedFriends.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // Search Messages
        builder.addCase(searchMessages.pending, (state) => {
            state.searchLoading = true;
            state.error = null;
        });
        builder.addCase(searchMessages.fulfilled, (state, action) => {
            state.searchLoading = false;
            state.searchResults = Array.isArray(action.payload.search_results) ? action.payload.search_results : ([]);
            state.error = null;
        });
        builder.addCase(searchMessages.rejected, (state, action) => {
            state.searchLoading = false;
            if (!action.meta.aborted) {
                state.error = action.error.message;
            }
        });
    },
});

export default MessagesSlice.reducer;
